/**
 * useSubscription Hook - Active Subscription Data
 *
 * Fetches the user's active subscription via the `get-subscription-status`
 * edge function. Returns plan details for display in the subscription
 * management UI.
 *
 * Migration (DTO contract, 2026-04-16):
 * Uses `fetchJsonValidated(SubscriptionStatusDto, ...)` so server responses are
 * validated against the shared contract. Backend/DB renames no longer drift
 * silently — validation fails fast instead.
 *
 * Uses the same useQuery pattern as useEntitlements.ts:
 * - Only fetches when user is authenticated
 * - 5-minute stale time (subscriptions change infrequently)
 * - Refetch on mount for cross-tab accuracy
 *
 * Task: NocoDB DD-156 (Subscription Management UI)
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { SubscriptionStatusDto, type TierId } from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { TIER_NAMES } from '../../../lib/polar/constants';
import type { BillingInterval } from '../../../lib/polar/constants';

export type { BillingInterval } from '../../../lib/polar/constants';

export interface SubscriptionData {
  /** The `get-subscription-status` endpoint does not return a DB id, kept optional for historical consumers. */
  id: string | undefined;
  providerSubscriptionId: string | null;
  tier: TierId;
  /** Billing cadence — not returned by the endpoint today; kept for UI type-compat. */
  billingInterval: BillingInterval | null;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Not returned by the endpoint today — kept optional for historical consumers. */
  canceledAt: string | null;
  provider: string | null;
  /** Not returned by the endpoint today — kept optional for historical consumers. */
  createdAt: string | null;
}

export interface UseSubscriptionReturn {
  subscription: SubscriptionData | null;
  /** Display name for the plan (e.g., "Drama+") */
  planName: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Query key factory
export const subscriptionKeys = {
  all: ['subscription'] as const,
  user: (userId: string | null) => [...subscriptionKeys.all, 'user', userId] as const,
};

/**
 * useSubscription - Fetch active subscription for the current user
 *
 * Only queries when user is authenticated. Returns null subscription
 * for anonymous/free users (they have no row in subscriptions table).
 */
export function useSubscription(): UseSubscriptionReturn {
  const { user, isRealUser } = useAuth();
  // Phase 2 (NocoDB #3870): use `isRealUser` instead of `!!accessToken` so anon
  // BA users don't fire a subscription fetch that always returns empty.
  const isAuthenticated = isRealUser;
  const userId = user?.id || null;

  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: subscriptionKeys.user(userId),
    queryFn: async (): Promise<SubscriptionData | null> => {
      if (!userId) return null;

      const dto = await fetchJsonValidated(
        SubscriptionStatusDto,
        getEdgeFunctionUrl('get-subscription-status'),
      );

      // No active subscription (free tier + never subscribed, or cancelled).
      if (!dto || dto.status === 'none') return null;

      return {
        id: undefined,
        providerSubscriptionId: dto.providerSubscriptionId ?? null,
        tier: dto.tier as TierId,
        billingInterval: null,
        status: dto.status,
        currentPeriodEnd: dto.currentPeriodEnd ?? null,
        cancelAtPeriodEnd: dto.cancelAtPeriodEnd ?? false,
        canceledAt: null,
        provider: dto.provider ?? null,
        createdAt: null,
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    enabled: isAuthenticated && !!userId,
    refetchOnMount: true,
  });

  const planName = subscription
    ? TIER_NAMES[subscription.tier] ?? subscription.tier
    : null;

  return {
    subscription: subscription ?? null,
    planName,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
